import { useEffect, useState, useMemo } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// In-memory cache (module-level) so multiple components that ask for the same
// surface in one render don't fan out to N requests.
const _cache = new Map(); // key: `${surface_id}:${variant||''}`  value: { ts, data }
const TTL_MS = 60 * 1000;

/**
 * Fetch an Airtable-driven UI surface (popup, page, section).
 * Returns { surface, slots, slotMap, ready, error, reload }.
 *   - slotMap is { [slot_key]: slotObject } for O(1) lookup.
 *
 * @param {string} surface_id  e.g. "popup_plan_makeup"
 * @param {object} options     { variant?: string, enabled?: boolean }
 */
export function useUiSurface(surface_id, { variant, enabled = true } = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const cacheKey = `${surface_id}:${variant || ""}`;

  const reload = async () => {
    if (!surface_id || !enabled) return;
    try {
      const url = variant
        ? `${API}/ui/surface/${surface_id}?variant=${encodeURIComponent(variant)}`
        : `${API}/ui/surface/${surface_id}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`surface fetch ${res.status}`);
      const json = await res.json();
      _cache.set(cacheKey, { ts: Date.now(), data: json });
      setData(json);
      setError(null);
    } catch (e) {
      setError(e.message || String(e));
    }
  };

  useEffect(() => {
    if (!surface_id || !enabled) return;
    const cached = _cache.get(cacheKey);
    if (cached && Date.now() - cached.ts < TTL_MS) {
      setData(cached.data);
      return;
    }
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, enabled]);

  const slotMap = useMemo(() => {
    const m = {};
    (data?.slots || []).forEach((s) => {
      if (s.slot_key) m[s.slot_key] = s;
    });
    return m;
  }, [data]);

  return {
    surface: data?.surface,
    slots: data?.slots || [],
    slotMap,
    ready: !!data,
    error,
    reload,
  };
}

/**
 * Resolve a slot value with a fallback. Works whether the surface is loaded or not.
 * Use everywhere you want to read Airtable copy with a safe local default.
 */
export function slotText(slotMap, key, fallback = "") {
  const s = slotMap?.[key];
  if (!s) return fallback;
  return s.body ?? s.default_value ?? fallback;
}
